import { combineReducers } from 'redux';
import { omit } from 'lodash';
import client from 'reducers/client';
import accounts from 'reducers/accounts';
import groups from 'reducers/groups';
import journal from 'reducers/journal';
import consolidations from 'reducers/consolidations';
import modals from 'reducers/modals';
import auth from 'reducers/auth';
import app from 'reducers/app';
import gridState from 'reducers/gridState';
import kpi from 'reducers/kpi';
import settings from 'reducers/settings';
import {
  ADD_PERIOD,
  ADD_SUBSIDIARY,
  IMPORT_GROUPS,
  MAP_ACCOUNTS,
  MAP_ACCOUNTS_TO_GROUPS,
  OPEN_CF,
  REMOVE_ACCOUNT,
  REMOVE_GROUP,
  REMOVE_PERIOD,
  REMOVE_SUBSIDIARY,
  RESTORE_PREVIOUS_STATE,
  UPDATE_SUBSIDIARY,
  ADD_EXCHANGE_RATE,
  EDIT_EXCHANGE_RATE,
  REMOVE_EXCHANGE_RATE,
  SAVE_EXCHANGE_RATE,
  ASSIGN_ACCOUNTS,
  ASSIGN_ACCOUNT,
  UNASSIGN_ACCOUNT,
  ADD_GROUP,
  MOVE_GROUP,
  MOVE_GROUP_UP,
  MOVE_GROUP_DOWN,
  ADD_ACCOUNT,
  IMPORT_ACCOUNTS,
  IMPORT_ACCOUNTS_WITH_ADDING,
  ADD_IMPORT,
  EDIT_ACCOUNT,
} from 'constants/actionTypes';
import {
  addPeriod,
  addSubsidiary,
  createCfObj,
  importGroups,
  mapAccounts,
  mapAccountsToGroups,
  removeAccount,
  removeGroup,
  removePeriod,
  removeSubsidiary,
  updateSubsidiary,
  addExchangeRate,
  editExchangeRate,
  removeExchangeRate,
  saveExchangeRate,
  refreshExchangeRates,
} from 'utils/stateToState';

const combinedReducer = combineReducers({
  client,
  accounts,
  groups,
  journal,
  consolidations,
  modals,
  auth,
  app,
  gridState,
  kpi,
  settings,
  // router: routerReducer
});

// https://redux.js.org/recipes/structuring-reducers/beyond-combinereducers#sharing-data-between-slice-reducers
function crossSliceReducer(state, action) {
  switch (action.type) {
    case OPEN_CF:
      const { cf } = action;
      // TODO: check client file version, convert if needed and validate
      return createCfObj(cf);
    case MAP_ACCOUNTS: {
      const { caItems } = action;
      return mapAccounts(state, caItems);
    }
    case MAP_ACCOUNTS_TO_GROUPS: {
      return mapAccountsToGroups(state);
    }
    case REMOVE_ACCOUNT: {
      const { accountId } = action;
      return removeAccount(state, accountId);
    }
    case REMOVE_GROUP: {
      const { groupId } = action;
      return removeGroup(state, groupId);
    }
    case IMPORT_GROUPS: {
      const { data, mapping } = action;
      return importGroups(state, data, mapping);
    }
    case ADD_PERIOD: {
      const { name, numberOfDays } = action;
      return addPeriod(state, name, numberOfDays);
    }
    case REMOVE_PERIOD: {
      const { periodId } = action;
      return removePeriod(state, periodId);
    }
    case ADD_SUBSIDIARY: {
      const { sub, cfObj } = action;
      return addSubsidiary(state, sub, cfObj);
    }
    case UPDATE_SUBSIDIARY: {
      const { subId, sub, cfObj } = action;
      return updateSubsidiary(state, subId, sub, cfObj);
    }
    case REMOVE_SUBSIDIARY: {
      const { subId } = action;
      return removeSubsidiary(state, subId);
    }
    case ADD_EXCHANGE_RATE: {
      return addExchangeRate(state);
    }
    case EDIT_EXCHANGE_RATE: {
      const { exchangeRate } = action;
      return editExchangeRate(state, exchangeRate);
    }
    case REMOVE_EXCHANGE_RATE: {
      const { exchangeRateId } = action;

      return removeExchangeRate(state, exchangeRateId);
    }
    case SAVE_EXCHANGE_RATE: {
      const { itemId, itemType, exchangeRateId } = action;

      return saveExchangeRate(state, itemId, itemType, exchangeRateId);
    }
    case RESTORE_PREVIOUS_STATE: {
      const hasPreviousState = !!state.previousState;
      return hasPreviousState
        ? {
            ...state,
            ...state.previousState,
          }
        : state;
    }
    default:
      return state;
  }
}

const erRefreshActionTypes = [
  ADD_ACCOUNT,
  EDIT_ACCOUNT,
  ASSIGN_ACCOUNT,
  ASSIGN_ACCOUNTS,
  UNASSIGN_ACCOUNT,
  IMPORT_ACCOUNTS,
  IMPORT_ACCOUNTS_WITH_ADDING,
  REMOVE_ACCOUNT,
  MAP_ACCOUNTS,
  MAP_ACCOUNTS_TO_GROUPS,
  ADD_GROUP,
  REMOVE_GROUP,
  MOVE_GROUP,
  MOVE_GROUP_UP,
  MOVE_GROUP_DOWN,
  ADD_IMPORT,
  IMPORT_GROUPS,
  REMOVE_EXCHANGE_RATE,
  SAVE_EXCHANGE_RATE,
];

function refreshExchangeRatesReducer(state, action) {
  if (erRefreshActionTypes.includes(action.type)) {
    return refreshExchangeRates(state);
  }

  return state;
}

export default function reducer(state, action) {
  const { previousState } = state;

  // previousState does not 'survive' combined reducer
  const intermediateState = combinedReducer(state, action);
  const intermediateStateWithPreviousState = {
    ...intermediateState,
    previousState,
  };
  const finalStateBeforeErRefresh = crossSliceReducer(
    intermediateStateWithPreviousState,
    action
  );
  const finalState = refreshExchangeRatesReducer(
    finalStateBeforeErRefresh,
    action
  );
  const newPreviousState = getNewPreviousState(state);

  return {
    ...finalState,
    previousState: newPreviousState,
  };
}

function getNewPreviousState(state) {
  const reducersToOmitInPreviousState = ['previousState', 'auth', 'app'];
  return { ...omit(state, reducersToOmitInPreviousState) };
}
